import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ChartLineUp, PaperPlaneTilt, UsersThree, Stack, Funnel, ShieldSlash, Upload, FlowArrow, ChartBar, ShieldCheck, StackSimple, Image as ImageIcon, Lightning, Sparkle } from "@phosphor-icons/react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const TABS = [
  { path: "/marketing",            label: "Overview",    icon: ChartLineUp,    exact: true,
    tip: "Snapshot of your contact base, recent campaigns, and best send-time recommendation." },
  { path: "/marketing/audience",   label: "Audience",    icon: UsersThree,
    tip: "Every marketing contact by what it is: active, with Sales, verified for release, never engaged, invalid… Click a count to inspect the contacts; retire or delete garbage from there." },
  { path: "/marketing/analytics",  label: "Analytics",   icon: ChartBar,
    tip: "Per-campaign and per-workflow funnel metrics — enrolled, sent, delivered, opened, clicked, bounced, replied. Click any row for the per-step drill-down with recent events." },
  { path: "/marketing/queue",      label: "Queue",       icon: PaperPlaneTilt,
    tip: "Every email currently scheduled to go out across all workflows + campaigns, sorted by due date in the recipient's local timezone." },
  { path: "/marketing/activity",   label: "Activity",    icon: Lightning,
    tip: "Global feed of every email Pulse has sent — filterable by lead, workflow, campaign, status, step, and date. Auto-refreshing single pane of glass for the dispatcher." },
  { path: "/marketing/workflows",  label: "Workflows",   icon: FlowArrow,
    tip: "Automated, multi-step email sequences triggered per contact — onboarding drips, re-engagement, lifecycle nurtures. Supports delays and if/else branches." },
  { path: "/marketing/forecast",   label: "Forecast",    icon: ChartLineUp,
    tip: "Live operator view of the workflow dispatcher — what was sent today, what's due in the next 24h, which contacts are at which step, and the per-step funnel. Built for spotting step starvation and queue drift fast." },
  { path: "/marketing/campaigns",  label: "Campaigns",   icon: PaperPlaneTilt,
    tip: "One-off broadcast emails sent to a list or segment — announcements, newsletters, launches. Supports A/B testing and scheduling." },
  { path: "/marketing/content-blocks", label: "Content Blocks", icon: StackSimple,
    tip: "Reusable HTML snippets (hero, CTA, testimonial, footer, …) droppable into any campaign's email body. Copy-paste the HTML into the content step of an editor." },
  { path: "/marketing/nurture-content", label: "Nurture Content", icon: Sparkle,
    tip: "Rotating library of short check-in emails for the long-term nurture workflow. Pulse AI writes drafts; only emails an admin has approved are ever sent." },
  { path: "/marketing/brand-assets", label: "Brand Assets", icon: ImageIcon,
    tip: "Centralised library for logos, email signatures, and social icons. Upload once, drop into any campaign or workflow email from the editor's Assets menu. Each asset remembers its click-through URL." },
  { path: "/marketing/contacts",   label: "Contacts",    icon: UsersThree,
    tip: "The single source of truth for everyone you can email — deduplicated across CSV imports, Clients, and CRM Leads. Stores consent status for compliance." },
  { path: "/marketing/lists",      label: "Lists",       icon: Stack,
    tip: "Hand-curated collections of contacts. Add or remove members manually. Useful for VIP audiences or event invitees." },
  { path: "/marketing/segments",   label: "Segments",    icon: Funnel,
    tip: "Rule-based audiences that update automatically. Filter by source, country, consent, or any combination with AND/OR groups." },
  { path: "/marketing/imports",    label: "CSV Imports", icon: Upload,
    tip: "Upload new contacts from a spreadsheet. Guided wizard maps columns, detects duplicates, and routes rows into the canonical contact table." },
  { path: "/marketing/compare",    label: "Compare",     icon: ChartBar,
    tip: "Side-by-side performance of two or more sent campaigns — open/click rates, device breakdown, link performance." },
  { path: "/marketing/approvals",  label: "Approvals",   icon: ShieldCheck,
    tip: "Admin approval queue for campaigns and workflows. No campaign or workflow can be sent or activated until a platform admin approves it." },
  { path: "/marketing/suppression",label: "Suppression", icon: ShieldSlash,
    tip: "Addresses that must never be emailed — unsubscribes, bounces, complaints. Enforced automatically on every send for compliance." },
  { path: "/marketing/settings",   label: "Settings",    icon: ShieldCheck,
    tip: "Conversion URL patterns (book-a-demo triggers) and other marketing-wide admin settings." },
];

export default function MarketingShell({ children }) {
  const nav = useNavigate();
  const loc = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [counts, setCounts] = useState({});

  // Lightweight poll of the approvals queue to surface the badge.
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const r = await api.get("/marketing/approvals/pending");
        if (cancelled) return;
        setPendingCount((r.data.campaigns?.length || 0) + (r.data.workflows?.length || 0));
      } catch { /* ignore */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Fetch per-tab record counts so the sub-nav shows `Workflows (2)`,
  // `Campaigns (1)`, etc. Single round-trip. Refreshes on route change
  // so creating / deleting a row in the active tab updates the badge
  // next time the user navigates across tabs.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/marketing/_tab-counts");
        if (!cancelled) setCounts(r.data || {});
      } catch { /* ignore — badges just stay blank on failure */ }
    })();
    return () => { cancelled = true; };
  }, [loc.pathname]);

  // Map tab path → count-key on the backend payload.
  const COUNT_KEY = {
    "/marketing/workflows": "workflows",
    "/marketing/campaigns": "campaigns",
    "/marketing/content-blocks": "content_blocks",
    "/marketing/brand-assets": "brand_assets",
    "/marketing/contacts": "contacts",
    "/marketing/lists": "lists",
    "/marketing/segments": "segments",
    "/marketing/imports": "imports",
    "/marketing/suppression": "suppression",
  };

  const isActive = (tab) => {
    if (tab.exact) return loc.pathname === tab.path || loc.pathname === tab.path + "/";
    return loc.pathname.startsWith(tab.path);
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-[#FAFAFA]">
      {/* Hero / sub-nav header */}
      <div className="border-b border-[#E5E5E5] bg-white" data-testid="marketing-shell">
        <div className="max-w-[1400px] mx-auto px-6 pt-6 pb-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">Marketing</p>
          <h1 className="text-2xl font-black tracking-tight text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Eyvo Pulse Campaigns
          </h1>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 overflow-x-auto">
          <div className="flex gap-1 border-b-0">
            <TooltipProvider delayDuration={200}>
              {TABS.map((t) => {
                const active = isActive(t);
                const Icon = t.icon;
                const showBadge = t.path === "/marketing/approvals" && pendingCount > 0;
                const countKey = COUNT_KEY[t.path];
                const n = countKey ? counts[countKey] : undefined;
                const btn = (
                  <button
                    onClick={() => nav(t.path)}
                    data-testid={`mkt-tab-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap ${
                      active
                        ? "border-[#0055FF] text-[#0055FF]"
                        : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
                    }`}
                  >
                    <Icon size={14} weight={active ? "fill" : "regular"} />
                    {t.label}
                    {typeof n === "number" && (
                      <span
                        className={`text-[10px] tabular-nums font-medium ${active ? "text-[#0055FF]" : "text-[#A3A3A3]"}`}
                        data-testid={`mkt-tab-count-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        ({n})
                      </span>
                    )}
                    {showBadge && (
                      <span
                        className="ml-1 bg-[#FFFBEB] text-[#92400E] text-[10px] font-bold px-1.5 py-0.5 tabular-nums"
                        data-testid="approvals-tab-badge"
                      >
                        {pendingCount}
                      </span>
                    )}
                  </button>
                );
                return t.tip ? (
                  <Tooltip key={t.path}>
                    <TooltipTrigger asChild>{btn}</TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      align="start"
                      sideOffset={6}
                      className="w-64 bg-[#0A0A0A] text-white border-[#0A0A0A] p-2.5 text-[11px] leading-snug"
                      data-testid={`mkt-tab-tip-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                        {t.label}
                      </span>
                      {t.tip}
                    </TooltipContent>
                  </Tooltip>
                ) : btn;
              })}
            </TooltipProvider>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-6">{children}</div>
    </div>
  );
}
